/** @jsxImportSource @emotion/react */
import { BrowserRouter as Router, useHistory } from 'react-router-dom';
import { Provider, useSelector } from 'react-redux';
import { SnackbarProvider } from 'notistack';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import { CSSObject, Global } from '@emotion/react';
import store from './state-manager/store';
import { resetCss } from './style/reset';
import { fontFaces, fontsCss } from './style/fonts';
import { formCss } from './style/form';
import { theme } from './theme';
import {
  useOnAuthenticatorChange,
  useHandlePrepareMainUserFailed,
} from './hooks/useOnAuthenticatorChange';
import useNotifier from './hooks/useNotifier';
import MainHeader, { MAIN_HEADER_HEIGHT } from './components/app/MainHeader';
import { AskUserView } from './components/commons/AskUser';
import { colors } from './style/colors';
import { getUserRoleSelector } from './state-manager/selectors/appSelectors';
import { LoginService } from './services/loginService';
import { useEffect } from 'react';
import { useSystemInfoFulfill } from './hooks/useSystemInfoFulfill';
import { captureForcedLiveSession } from './hooks/useActiveSessionGuard';
import { useEmrExtension } from './hooks/useEmrExtension';
import { setSafeSessionContext } from './observability/rum/rumClient';
import { useRumPageView } from './observability/rum/useRumPageView';
import { RumErrorBoundary } from './observability/rum/RumErrorBoundary';
import AppRoutes from './components/app/AppRoutes';
import PatientTermsGate from './components/terms-of-use/PatientTermsGate';

const appGlobalCss: CSSObject = {
  'body, html': {
    height: '100%',
    fontFamily: '"IntelOne Text", sans-serif',
    overflowY: 'auto',
    backgroundColor: colors.appBackground,
  },
  html: {
    overflow: 'hidden',
  },
  '#root': {
    height: '100%',
  },
};

function App() {
  useNotifier();
  useOnAuthenticatorChange();
  useHandlePrepareMainUserFailed();
  useSystemInfoFulfill();
  useEmrExtension();

  // Local-dev only, and a no-op off localhost: capture `?liveSession=1` wherever it is set, before
  // the in-app navigations to the plan editor rebuild the query string and drop it. Doing this only
  // inside useActiveSessionGuard is too late — the editor is its sole consumer.
  useEffect(() => {
    captureForcedLiveSession();
  }, []);

  const history = useHistory();
  const role = useSelector(getUserRoleSelector);

  // RUM is initialized at bootstrap in AppWithAuth (before the auth gate) so
  // pre-login errors are captured; here we enrich the session, once logged in,
  // so every error names WHICH USER, CLINIC, and ROLE without a second lookup —
  // for ANY logged-in user (clinician or patient). All values come from the
  // Cognito session (LoginService): appUserId is the OPAQUE Cognito id (no
  // plaintext PII — email/name are deliberately not sent), clinicId is the tenant
  // id (resolved to a clinic name in the EOD report). No Redux, no extra API call.
  // Empty values are dropped by the sanitizer; this re-stamps when role lands.
  // setSafeSessionContext is a no-op when RUM is disabled.
  useEffect(() => {
    const login = LoginService.getInstance();
    setSafeSessionContext({
      clinicianRole: role || undefined,
      appUserId: login.getUserId(),
      clinicId: login.getTenantId(),
    });
  }, [role]);

  useRumPageView();

  // Prevent the browser's scroll-wheel-changes-a-focused-number-input behavior. When a
  // <input type="number"> has focus and the user scrolls the page, the browser increments/
  // decrements its value by `step` (default 1) — so a field silently drifts (e.g. 90 -> 89) while
  // the clinician is doing something else entirely. This bit both the Custom Activity editor and
  // the Treatment Plan forms. Blurring the focused number input on wheel neutralizes it globally
  // for every number input in the portal, with no per-input changes. Capture phase so it runs
  // before the input's own default handling.
  useEffect(() => {
    const onWheel = (e: WheelEvent) => {
      const el = document.activeElement;
      if (
        el instanceof HTMLInputElement &&
        el.type === 'number' &&
        el === e.target
      ) {
        el.blur();
      }
    };
    document.addEventListener('wheel', onWheel, { capture: true });
    return () =>
      document.removeEventListener('wheel', onWheel, { capture: true });
  }, []);

  useEffect(() => {
    if (role === 'patient') {
      const searchParams = new URLSearchParams(history.location.search);
      searchParams.delete('patient-id');
      history.replace({
        pathname: '/patient',
        search: searchParams.toString(),
      });
    } else {
      const pathname = history.location.pathname;
      if (pathname === '/') {
        history.replace({
          pathname: '/patients',
          search: history.location.search,
        });
      }
    }
  }, [role]);

  return (
    <RumErrorBoundary componentName="app-root">
      <div
        css={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          height: '100%',
        }}
      >
        <Global
          styles={[resetCss, appGlobalCss, fontFaces, fontsCss, formCss]}
        />
        <MainHeader />
        <div
          css={{
            height: `calc(100% - ${MAIN_HEADER_HEIGHT})`,
            flexGrow: 1,
          }}
        >
          <PatientTermsGate>
            <AppRoutes />
          </PatientTermsGate>
        </div>
        <AskUserView />
      </div>
    </RumErrorBoundary>
  );
}

export default function WrappedApp() {
  return (
    <Router>
      <ThemeProvider theme={theme}>
        <SnackbarProvider maxSnack={3} autoHideDuration={6000}>
          <Provider store={store}>
            <App />
          </Provider>
        </SnackbarProvider>
      </ThemeProvider>
    </Router>
  );
}
